import { createContext, useContext } from "react";

export const BOOKING_URL = "https://www.booking.com/hotel/it/kings-place.it.html";
export const ADDRESS = "Via Giovanni Vitelleschi 6, 00193 Roma";

export const LangContext = createContext(null);
export const useLang = () => useContext(LangContext);

export const translations = {
  it: {
    nav: {
      rooms: "Camere",
      amenities: "Servizi",
      gallery: "Galleria",
      surroundings: "Dintorni",
      reviews: "Recensioni",
      contact: "Contatti",
      book: "Prenota",
    },
    hero: {
      overline: "Guest House di Lusso — Vaticano Prati, Roma",
      line1: "Vivi il comfort",
      line2: "regale",
      line3: "nel cuore di Roma",
      sub: "King's Place offre un rifugio elegante a pochi passi da San Pietro e Castel Sant'Angelo. Un'esperienza di soggiorno indimenticabile nella Città Eterna.",
      book: "Prenota su Booking",
      discover: "Scopri le nostre camere",
      availability: "Richiedi disponibilità",
      stats: [
        { value: "9.4", label: "Wonderful su Booking" },
        { value: "9.8", label: "Punteggio posizione" },
        { value: "212", label: "Recensioni verificate" },
      ],
      scroll: "Scorri",
    },
    marquee: ["Vaticano Prati", "Roma", "A due passi da San Pietro", "King's Place", "Ospitalità romana"],
    manifesto: {
      overline: "La nostra filosofia",
      title: "Tre idee semplici, una sola casa",
      chapters: [
        {
          n: "01",
          title: "L'arte di abitare Roma",
          text: "King's Place nasce al primo piano di un palazzo signorile del quartiere Prati, tra le vie dello shopping e la cupola di San Pietro. Non un hotel, ma una casa: pochi ambienti curati, luce naturale, silenzio.",
        },
        {
          n: "02",
          title: "Comfort curato nei dettagli",
          text: "Parquet, biancheria fresca, aria condizionata, smart TV e una macchina del caffè sempre pronta nella cucina condivisa. Ogni dettaglio è pensato per farti sentire a casa tua.",
        },
        {
          n: "03",
          title: "La città fuori dalla porta",
          text: "Piazza San Pietro a 7 minuti, i Musei Vaticani a 9, Castel Sant'Angelo a 12. Il resto di Roma si raggiunge a piedi, un caffè alla volta.",
        },
      ],
    },
    rooms: {
      overline: "Le camere",
      title: "Le nostre camere",
      note: "Tre tipologie di camera pensate per ogni esigenza, dal comfort familiare all'eleganza per coppie. Tutte con bagno privato, WiFi gratuito, aria condizionata, smart TV e set per tè e caffè.",
      cta: "Prenota questa camera",
      items: [
        {
          key: "familiare",
          name: "Camera Familiare",
          guests: "Fino a 4 ospiti",
          bed: "1 letto queen · 1 singolo · 1 divano letto",
          desc: "Spaziosa e luminosa, con il murale della skyline romana disegnato a mano: perfetta per famiglie e piccoli gruppi.",
        },
        {
          key: "matrimoniale",
          name: "Ampia Camera Matrimoniale",
          guests: "2 ospiti",
          bed: "1 letto queen",
          desc: "Ampia e luminosa, con parete botanica di fiori e farfalle e bagno privato di design: pensata per le coppie che vogliono vivere Roma a piedi.",
        },
        {
          key: "singola",
          name: "Camera Singola con Terrazza",
          guests: "1 ospite",
          bed: "1 letto singolo",
          desc: "Un rifugio silenzioso con terrazza affacciata sul cortile interno: ideale per chi viaggia da solo.",
        },
      ],
    },
    amenities: {
      overline: "Servizi",
      title: "Comfort e servizi senza pari",
      desc: "Ogni dettaglio è curato per un soggiorno indimenticabile. Scoprite i nostri servizi pensati per il vostro benessere e comfort.",
      items: [
        { title: "Terrazzo comune", text: "Uno spazio all'aperto con tenda a vela e zona salotto per rilassarsi" },
        { title: "Wi-Fi alta velocità", text: "Connessione gratuita e veloce nelle camere e nelle aree comuni" },
        { title: "Camere climatizzate", text: "Aria condizionata e riscaldamento per il massimo comfort" },
        { title: "Comfort e qualità", text: "Materassi premium e biancheria di alta qualità per un riposo regale" },
        { title: "Cucina comune", text: "Fornelli, forno, lavabo e tutti gli utensili necessari a disposizione" },
        { title: "Smart TV", text: "Con i tuoi servizi di streaming preferiti" },
      ],
    },
    gallery: {
      overline: "Galleria",
      title: "La nostra Galleria",
      desc: "Esplorate i nostri eleganti ambienti attraverso le immagini. Dalle camere accoglienti agli spazi comuni raffinati, ogni dettaglio è curato per il vostro comfort.",
    },
    surroundings: {
      overline: "Attrazioni",
      title: "Il meglio di Roma a portata di mano",
      desc: "Scoprite meraviglie senza tempo, tutte raggiungibili con una breve passeggiata dalla nostra guest house. La storia di Roma vi aspetta.",
      note: "Tempi di percorrenza a piedi dalla struttura",
      walkBadge: "a piedi",
      attractions: [
        { img: "/assets/surroundings/castel.jpg", name: "Castel Sant'Angelo", time: "12 min", text: "L'imponente mausoleo di Adriano, oggi museo e punto panoramico mozzafiato sulla Città Eterna." },
        { img: "/assets/surroundings/sanpietro.jpg", name: "Basilica di San Pietro", time: "7 min", text: "Il cuore del Cristianesimo mondiale, con la maestosa cupola di Michelangelo e i Musei Vaticani." },
        { img: "/assets/surroundings/ponte.jpg", name: "Ponte Sant'Angelo", time: "11 min", text: "Il ponte degli angeli di Bernini, che collega il centro storico con il Vaticano attraverso il Tevere." },
      ],
      items: [
        { place: "Piazza San Pietro", time: "7 min" },
        { place: "Musei Vaticani", time: "9 min" },
        { place: "Castel Sant'Angelo", time: "12 min" },
        { place: "Metro Ottaviano", time: "5 min" },
        { place: "Piazza Navona", time: "20 min" },
        { place: "Pantheon", time: "25 min" },
      ],
      transportLabel: "Trasporti",
      transport: "Metro Ottaviano a 5 min a piedi · Stazione Termini raggiungibile in metro",
      directions: "Ottieni indicazioni",
    },
    reviews: {
      overline: "Recensioni",
      title: "Cosa dicono i nostri ospiti",
      scoreLabel: "Eccezionale",
      basedOn: "Basato su 212 recensioni verificate su Booking.com",
      verified: "Recensione verificata — Booking.com",
      readAll: "Leggi tutte le recensioni su Booking",
      categories: [
        { label: "Posizione", value: 9.8 },
        { label: "Comfort", value: 9.7 },
        { label: "Pulizia", value: 9.6 },
        { label: "Staff", value: 9.6 },
        { label: "Servizi", value: 9.4 },
        { label: "Qualità-prezzo", value: 9.2 },
      ],
      quotes: [
        { name: "Silvia", country: "Italia", score: "10", room: "Camera Familiare", text: "La nostra permanenza è stata davvero piacevole! La stanza era impeccabilmente pulita, la posizione perfetta per visitare la zona e l'host sempre gentile, disponibile e rapido nel rispondere. Torneremo sicuramente con piacere." },
        { name: "Simona", country: "Italia", score: "10", room: "Camera Familiare", text: "Vicinissima alla Basilica di San Pietro e a Castel Sant'Angelo… da subito si nota la pulizia impeccabile e la ristrutturazione fatta da poco. Check-in velocissimo grazie ad un host gentilissimo e disponibilissimo." },
        { name: "Mauro", country: "Svizzera", score: "10", room: "Camera Singola con Terrazza", text: "Struttura veramente accogliente e host molto cortese e ospitale. A due passi dal Vaticano e dal centro di Roma. Consiglio vivamente a chi vuole un luogo di pace e tranquillità in zona Prati." },
        { name: "Marco", country: "Italia", score: "10", room: "Camera Familiare", text: "Posizione ideale vicino a Castel Sant'Angelo, a due passi da Piazza San Pietro. La stanza grande, luminosa e pulitissima, con un bel bagno. Comunicazione perfetta prima dell'arrivo, staff gentilissimo." },
        { name: "Rosa", country: "Italia", score: "10", room: "Ampia Camera Matrimoniale", text: "La posizione eccellente, stanza grande e ottima possibilità di usare la cucina in comune. Supermercato sotto casa, vicinissimo al Vaticano e a Piazza Cavour." },
        { name: "Marco", country: "Italia", score: "9,0", room: "Ampia Camera Matrimoniale", text: "Posizione centralissima e bella camera. Il posto è pulitissimo, il letto molto comodo e soprattutto l'host molto disponibile e reattivo!" },
        { name: "Chiara", country: "Italia", score: "10", room: "Ampia Camera Matrimoniale", text: "Zona tranquilla con vista suggestiva." },
      ],
    },
    contact: {
      overline: "Contatti",
      title: "Richiedi informazioni sul tuo soggiorno",
      desc: "Siamo qui per aiutarvi a pianificare la vostra vacanza romana perfetta. Compila il modulo e ti risponderemo entro 24 ore.",
      addressLabel: "Indirizzo",
      checkinLabel: "Check-in",
      checkin: "14:00 — 23:30",
      checkoutLabel: "Check-out",
      checkout: "06:00 — 10:30",
      hostLabel: "Il tuo host",
      host: "Filippo",
      form: {
        name: "Nome completo *",
        email: "Email *",
        phone: "Telefono *",
        arrival: "Data di arrivo *",
        departure: "Data di partenza *",
        message: "Messaggio (opzionale)",
        privacy: "* Campi obbligatori. Rispettiamo la tua privacy e non condivideremo mai i tuoi dati.",
        forward: "Le richieste saranno inoltrate a info@kingsplace.it",
        submit: "Invia richiesta",
        sending: "Invio in corso…",
        success: "Richiesta inviata! Ti risponderemo entro 24 ore.",
        error: "Qualcosa è andato storto. Riprova o prenota su Booking.",
      },
    },
    footer: {
      bookCta: "Prenota il tuo soggiorno",
      exploreTitle: "Esplora",
      contactTitle: "Contatti",
      rights: "© 2026 King's Place — Guest House, Roma",
      license: "Licenza 058091-AFF-05103 · IT058091B4DAIE8ULP",
    },
    whatsapp: {
      label: "Scrivici su WhatsApp",
      prefill: "Ciao Filippo! Ti scrivo dal sito di King's Place, vorrei informazioni per un soggiorno.",
    },
  },
  en: {
    nav: {
      rooms: "Rooms",
      amenities: "Amenities",
      gallery: "Gallery",
      surroundings: "Neighbourhood",
      reviews: "Reviews",
      contact: "Contact",
      book: "Book now",
    },
    hero: {
      overline: "Luxury Guest House — Vaticano Prati, Rome",
      line1: "Experience royal",
      line2: "comfort",
      line3: "in the heart of Rome",
      sub: "King's Place offers an elegant retreat just steps from St Peter's and Castel Sant'Angelo. An unforgettable stay in the Eternal City.",
      book: "Book on Booking.com",
      discover: "Discover our rooms",
      availability: "Check availability",
      stats: [
        { value: "9.4", label: "Wonderful on Booking" },
        { value: "9.8", label: "Location score" },
        { value: "212", label: "Verified reviews" },
      ],
      scroll: "Scroll",
    },
    marquee: ["Vaticano Prati", "Rome", "A stone's throw from St Peter's", "King's Place", "Roman hospitality"],
    manifesto: {
      overline: "Our philosophy",
      title: "Three simple ideas, one single home",
      chapters: [
        {
          n: "01",
          title: "The art of living in Rome",
          text: "King's Place sits on the first floor of an elegant building in the Prati district, between the shopping streets and St Peter's dome. Not a hotel, but a home: a few curated rooms, natural light, silence.",
        },
        {
          n: "02",
          title: "Comfort in every detail",
          text: "Parquet floors, fresh linen, air conditioning, smart TV and a coffee machine always ready in the shared kitchen. Every detail is designed to make you feel at home.",
        },
        {
          n: "03",
          title: "The city outside your door",
          text: "St Peter's Square in 7 minutes, the Vatican Museums in 9, Castel Sant'Angelo in 12. The rest of Rome is within walking distance, one espresso at a time.",
        },
      ],
    },
    rooms: {
      overline: "The rooms",
      title: "Our rooms",
      note: "Three room types designed for every need, from family comfort to elegance for couples. All with private bathroom, free WiFi, air conditioning, smart TV and a tea & coffee set.",
      cta: "Book this room",
      items: [
        {
          key: "familiare",
          name: "Family Room",
          guests: "Up to 4 guests",
          bed: "1 queen bed · 1 twin · 1 sofa bed",
          desc: "Spacious and bright, with a hand-drawn mural of the Roman skyline: perfect for families and small groups.",
        },
        {
          key: "matrimoniale",
          name: "Large Double Room",
          guests: "2 guests",
          bed: "1 queen bed",
          desc: "Large and bright, with a botanical wall of flowers and butterflies and a designer private bathroom: made for couples who want to explore Rome on foot.",
        },
        {
          key: "singola",
          name: "Single Room with Terrace",
          guests: "1 guest",
          bed: "1 twin bed",
          desc: "A quiet retreat with a terrace overlooking the inner courtyard: ideal for solo travellers.",
        },
      ],
    },
    amenities: {
      overline: "Amenities",
      title: "Unparalleled comfort and services",
      desc: "Every detail is designed for an unforgettable stay. Discover our services created for your wellbeing and comfort.",
      items: [
        { title: "Shared terrace", text: "An open-air space with a sail canopy and lounge area to unwind" },
        { title: "High-speed Wi-Fi", text: "Free, fast connection in the rooms and common areas" },
        { title: "Air-conditioned rooms", text: "Air conditioning and heating for maximum comfort" },
        { title: "Comfort & quality", text: "Premium mattresses and high-quality linen for a royal rest" },
        { title: "Shared kitchen", text: "Hob, oven, sink and all the utensils you need at your disposal" },
        { title: "Smart TV", text: "With your favourite streaming services" },
      ],
    },
    gallery: {
      overline: "Gallery",
      title: "Our Gallery",
      desc: "Explore our elegant spaces through images. From cosy rooms to refined common areas, every detail is designed for your comfort.",
    },
    surroundings: {
      overline: "Attractions",
      title: "The best of Rome within reach",
      desc: "Discover timeless wonders, all reachable with a short walk from our guest house. The history of Rome awaits you.",
      note: "Walking times from the property",
      walkBadge: "on foot",
      attractions: [
        { img: "/assets/surroundings/castel.jpg", name: "Castel Sant'Angelo", time: "12 min", text: "Hadrian's imposing mausoleum, today a museum with breathtaking panoramic views over the Eternal City." },
        { img: "/assets/surroundings/sanpietro.jpg", name: "St Peter's Basilica", time: "7 min", text: "The heart of world Christianity, with Michelangelo's majestic dome and the Vatican Museums." },
        { img: "/assets/surroundings/ponte.jpg", name: "Ponte Sant'Angelo", time: "11 min", text: "Bernini's bridge of angels, connecting the historic centre with the Vatican across the Tiber." },
      ],
      items: [
        { place: "St Peter's Square", time: "7 min" },
        { place: "Vatican Museums", time: "9 min" },
        { place: "Castel Sant'Angelo", time: "12 min" },
        { place: "Ottaviano Metro", time: "5 min" },
        { place: "Piazza Navona", time: "20 min" },
        { place: "Pantheon", time: "25 min" },
      ],
      transportLabel: "Transport",
      transport: "Ottaviano Metro 5 min on foot · Termini Station reachable by metro",
      directions: "Get directions",
    },
    reviews: {
      overline: "Reviews",
      title: "What our guests say",
      scoreLabel: "Wonderful",
      basedOn: "Based on 212 verified reviews on Booking.com",
      verified: "Verified review — Booking.com",
      readAll: "Read all reviews on Booking",
      categories: [
        { label: "Location", value: 9.8 },
        { label: "Comfort", value: 9.7 },
        { label: "Cleanliness", value: 9.6 },
        { label: "Staff", value: 9.6 },
        { label: "Facilities", value: 9.4 },
        { label: "Value for money", value: 9.2 },
      ],
      quotes: [
        { name: "Silvia", country: "Italy", score: "10", room: "Family Room", text: "Our stay was truly pleasant! The room was impeccably clean, the location perfect for exploring the area, and the host always kind, helpful and quick to reply. We will definitely come back." },
        { name: "Simona", country: "Italy", score: "10", room: "Family Room", text: "Very close to St. Peter's Basilica and Castel Sant'Angelo… you immediately notice the impeccable cleanliness and the recent renovation. Super fast check-in thanks to an extremely kind and helpful host." },
        { name: "Mauro", country: "Switzerland", score: "10", room: "Single Room with Terrace", text: "A truly welcoming property with a very courteous and hospitable host. A stone's throw from the Vatican and the centre of Rome. Highly recommended for anyone seeking peace and quiet in the Prati district." },
        { name: "Marco", country: "Italy", score: "10", room: "Family Room", text: "Ideal location near Castel Sant'Angelo, a short walk from St. Peter's Square. The room is large, bright and spotless, with a lovely bathroom. Perfect communication before arrival, extremely kind staff." },
        { name: "Rosa", country: "Italy", score: "10", room: "Large Double Room", text: "Excellent location, large room and great option to use the shared kitchen. Supermarket right downstairs, very close to the Vatican and Piazza Cavour." },
        { name: "Marco", country: "Italy", score: "9.0", room: "Large Double Room", text: "Super central location and a beautiful room. The place is spotless, the bed very comfortable and above all the host is very helpful and responsive!" },
        { name: "Chiara", country: "Italy", score: "10", room: "Large Double Room", text: "Quiet area with an evocative view." },
      ],
    },
    contact: {
      overline: "Contact",
      title: "Request information about your stay",
      desc: "We are here to help you plan your perfect Roman holiday. Fill in the form and we will reply within 24 hours.",
      addressLabel: "Address",
      checkinLabel: "Check-in",
      checkin: "2:00 PM — 11:30 PM",
      checkoutLabel: "Check-out",
      checkout: "6:00 AM — 10:30 AM",
      hostLabel: "Your host",
      host: "Filippo",
      form: {
        name: "Full name *",
        email: "Email *",
        phone: "Phone *",
        arrival: "Arrival date *",
        departure: "Departure date *",
        message: "Message (optional)",
        privacy: "* Required fields. We respect your privacy and will never share your data.",
        forward: "Requests will be forwarded to info@kingsplace.it",
        submit: "Send request",
        sending: "Sending…",
        success: "Request sent! We will reply within 24 hours.",
        error: "Something went wrong. Please try again or book on Booking.com.",
      },
    },
    footer: {
      bookCta: "Book your stay",
      exploreTitle: "Explore",
      contactTitle: "Contact",
      rights: "© 2026 King's Place — Guest House, Rome",
      license: "License 058091-AFF-05103 · IT058091B4DAIE8ULP",
    },
    whatsapp: {
      label: "Chat on WhatsApp",
      prefill: "Hi Filippo! I'm writing from the King's Place website, I'd like some information about a stay.",
    },
  },
};
